/* eslint-disable react/prop-types */
// 入参契约见 JSDoc 与 docs/offline-material-frontend-contract.md（仓库未将 prop-types 列为直接依赖）
/**
 * 二维码大图预览 + 单张下载
 *
 * 需求：【操作】查看图片 —— 点击二维码，查看大图，并可下载当前图片，图片命名为「二维码+code」
 *
 * 🔴 单张下载**不调任何接口**，走端侧 blob（2026-09-07 定稿，依据见契约 §4.3、§11.9）：
 *   看大图与下载用的是同一个 `imageUrl`（永久直链）。因为图片在 COS/CDN 属跨源，
 *   直接把 `a[download]` 指向它**不会下载**：实测（headless Chrome + CDP）零下载事件，
 *   且页面会被**导航到图片地址**（后台 SPA 状态当场丢）；
 *   所以先把字节 fetch 成 blob、换成同源 objectURL 再点，才拿得住「二维码+code」这个命名（已实测落盘）。
 *   前提（已实测）：图片域名放开 CORS。
 *
 * ⚠️ 与契约 v1.2 §3.5 的偏离（有意为之，别当缺陷「修回去」）：
 *   §3.5 原要求「点下载那一刻现调 GET /material/code/image 拿 10 分钟签名链」。
 *   该接口 2026-09-07 SIT 实测 GET/POST 均 404（未部署，见 §7.2 D6）→ 一级路径不可用，
 *   用户每次点下载都只会看到「获取下载链接失败，请重试」。故改走端侧 blob。
 *   取链代码没有删：`services/offlineMaterial.js#queryQrcodeImage` + 本文件 `downloadQrcode`
 *   的 `downloadUrl` 入参都在。后端部署完、且确认「文件名一律用服务端 fileName」（§8-A3）后，
 *   在 `handleDownload` 里把取链那一步加回来即可（契约 §11.9 记了这一行的位置）。
 *
 * ⚠️ 图片必须用服务端产出的微信小程序码 PNG（720×720、印刷级）。
 *    禁止用 qrcode.react 按 code 前端重绘：那和后端下发的成品图不是同一张，印刷即事故。
 *
 * 复用说明：同事的「物料生成记录页」「二维码绑定详情页」直接 import 本组件，
 *          不要复制实现，否则文件名规则会漂移。
 */
import React, { useState } from 'react';
import { Modal, Button, Typography, message } from 'antd';
import { QRCODE_FILE_NAME, TEXT } from '../../constants';

const IMAGE_SIZE = 300;

/** 触发一次 a[download] 点击 */
const triggerAnchor = (href, fileName, openInNewTab) => {
  const link = document.createElement('a');
  link.style.display = 'none';
  link.href = href;
  if (fileName) {
    link.download = fileName;
  }
  // 跨域且服务端没给 Content-Disposition 时，浏览器会忽略 download 变成导航；
  // 新开标签可保证不把后台当前页面顶掉（SPA 状态会丢）。
  if (openInNewTab) {
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * 下载单张二维码（三级优先；🔴 当前唯一调用方 QrcodePreviewModal 不传 downloadUrl，所以实走 ②）
 *
 * ① downloadUrl（接口可用时的首选）：带 `Content-Disposition: attachment; filename="二维码xxx.png"`
 *    的签名直链。端侧只需点一下 a 标签，无需 CORS、不走 blob、不占内存，文件名由服务端定。
 * ② 只有 imageUrl（**现在的主路径**）：跨域 URL 上浏览器会**忽略** a[download]，
 *    所以先 fetch→blob 转成同源 objectURL，才能保住「二维码+code」这个命名。
 *    前提是图片域名放开 CORS（`Access-Control-Allow-Origin`）——2026-09-07 实测生产 COS 桶与
 *    CDN 域名均已放开（桶级规则，见契约 §11.9）；换环境时按 §11.9 的 curl 复核一次。
 * ③ 兜底：直接新开窗口打开图片，文件名不可控，并提示用户右键另存。
 *    CORS 没配好的环境会落到这里（不白屏、不误报成功），但命名会丢。
 *
 * @param {Object}   options
 * @param {string}   [options.downloadUrl] 带 attachment 响应头的下载地址（优先，来自 §3.5 接口）
 * @param {string}   [options.imageUrl]    图片直链（用于展示，也可当作下载源）
 * @param {string}   options.fileName      期望文件名，如 `二维码rumnqzgol46.png`
 * @return {Promise<boolean>} 是否走了「文件名可控」的下载路径
 */
export const downloadQrcode = async ({
  downloadUrl,
  imageUrl,
  fileName,
} = {}) => {
  const url = downloadUrl || imageUrl;
  if (!url) {
    message.warning(TEXT.NO_IMAGE_TOAST);
    return false;
  }
  if (downloadUrl) {
    // 文件名交给服务端的 Content-Disposition，download 属性只是锦上添花
    triggerAnchor(downloadUrl, fileName, true);
    return true;
  }
  try {
    const resp = await fetch(imageUrl, { mode: 'cors' });
    if (!resp.ok) {
      throw new Error(`download failed: ${resp.status}`);
    }
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    // 🔴 blob URL 同源，download 属性里的中文文件名才会被浏览器采纳；不开新标签——
    //    a[target=_blank] 依赖「当前有效的用户手势」，fetch 一慢就被 Chrome 静默丢掉（契约 §11.8），
    //    而同源 blob 走同标签不需要手势（§11.8 实验 #3/#4 实测）。
    triggerAnchor(objectUrl, fileName, false);
    // 立刻 revoke 会截断仍在写盘的下载（Safari/Firefox 尤其明显），延后回收，同 §11.8 iframe 做法
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30 * 1000);
    return true;
  } catch (error) {
    window.open(imageUrl);
    message.warning(TEXT.COPY_FAIL_TOAST);
    return false;
  }
};

const QrcodePreviewModal = (props) => {
  const {
    visible = false,
    code = '',
    imageUrl = '',
    onCancel,
    showDownload = true,
  } = props;
  const [loadFailed, setLoadFailed] = useState(false);
  // 点下载后、签名链未返回前的 loading：防重复点击，也免得用户以为没点上
  const [fetching, setFetching] = useState(false);

  const handleClose = () => {
    setLoadFailed(false);
    if (onCancel) {
      onCancel();
    }
  };

  /**
   * 下载当前图片：不调任何接口，直接把大图直链读成 blob 再落盘（命名「二维码+code」）。
   * 唯一的异步是 fetch 图片字节本身，loading 盖住它，免得用户以为没点上而反复点。
   * downloadQrcode 内部已做失败兜底（③ 新窗口 + toast），不会抛异常。
   *
   * ⚠️ 恢复一级路径（后端部署完 /material/code/image 后）：先 `await queryQrcodeImage(code)`，
   *    再传 `downloadUrl: res.data.qrcodeDownloadUrl` + `fileName: res.data.fileName || QRCODE_FILE_NAME(code)`。
   */
  const handleDownload = async () => {
    if (fetching) {
      return;
    }
    if (!code || !imageUrl || loadFailed) {
      message.warning(TEXT.NO_IMAGE_TOAST);
      return;
    }
    setFetching(true);
    try {
      // 成功时不另发提示（与改动前一致，浏览器自身有下载条）；
      // 失败时 downloadQrcode 内部已走 ③（新窗口打开 + 右键另存 toast）。
      await downloadQrcode({
        imageUrl,
        // 🔴 前端命名：规则与需求原文一致（二维码+code.png），与 mock / 服务端口径同值
        fileName: QRCODE_FILE_NAME(code),
      });
    } finally {
      setFetching(false);
    }
  };

  return (
    <Modal
      title="查看图片"
      visible={visible}
      width={IMAGE_SIZE + 100}
      maskClosable
      destroyOnClose
      onCancel={handleClose}
      footer={
        showDownload
          ? [
              <Button key="close" onClick={handleClose}>
                关闭
              </Button>,
              <Button
                key="download"
                type="primary"
                loading={fetching}
                disabled={!imageUrl || loadFailed}
                onClick={handleDownload}
              >
                下载
              </Button>,
            ]
          : null
      }
    >
      <div style={{ textAlign: 'center' }}>
        {imageUrl && !loadFailed ? (
          <img
            src={imageUrl}
            alt={code || '二维码'}
            width={IMAGE_SIZE}
            height={IMAGE_SIZE}
            onError={() => setLoadFailed(true)}
            style={{
              background: '#fff',
              border: '1px solid #f0f0f0',
              borderRadius: 4,
              objectFit: 'contain',
            }}
          />
        ) : (
          <div style={{ color: '#999', padding: '60px 0' }}>
            二维码图片暂不可用，可联系产研获取原图
          </div>
        )}
        {code ? (
          <div style={{ marginTop: 12 }}>
            <Typography.Text copyable={{ text: code }}>{code}</Typography.Text>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default QrcodePreviewModal;
